import { FC, useCallback, useEffect, useMemo } from 'react';
import type { CascaderProps } from 'antd';
import { Alert, Cascader, Skeleton, Tooltip } from 'antd';
import { groupItemsByCategory } from 'packages/omnitag/utils';
import { SearchOutlined } from '@ant-design/icons';
import { HorizLayout } from 'packages/omnitag/text-search/search-top-panel';
import * as H from 'history';
import 'packages/omnitag/text-search/search-ui.css';
import {
  generatePath,
  useHistory,
  useRouteMatch,
  useParams,
} from 'react-router-dom';
import { useChangeURLParams } from 'packages/lib/utils/url';
import { searchPageNoAtom, searchPageSizeAtom } from 'packages/omnitag/state';
import { useAtomValue, useSetAtom } from 'jotai';
import { useGetSearchTagsAPI } from 'packages/omnitag/api/use-search-tags-api';
import { requestIDAtom } from 'packages/omnitag/state/text-search';
import useSWR from 'swr';
import { getTenantName } from 'packages/lib/auth';
import { useTourRef } from 'packages/omnitag/hooks/use-tour-refs';

interface Option {
  value: string | number;
  label: string;
  children?: Option[];
  disableCheckbox?: boolean;
}

export const SearchUI: FC = (): JSX.Element => {
  const match = useRouteMatch();
  const history = useHistory();
  const changeURLParams = useChangeURLParams();
  const { query } = useParams<{ query?: string }>();
  const searchTextDecoded = decodeURIComponent(query ?? '')?.replace(
    /\+/g,
    ' '
  );
  const setPageNo = useSetAtom(searchPageNoAtom);
  const pageSize = useAtomValue(searchPageSizeAtom);
  const requestID = useAtomValue(requestIDAtom);
  const { data: tenant } = useSWR('tenant-name', getTenantName);
  const isContractorToken = tenant === 'contractor';
  const searchTagsSWR = useGetSearchTagsAPI();
  const searchRef = useTourRef<HTMLDivElement>('search-ui');

  const tagsObj = useMemo(
    () => groupItemsByCategory(searchTagsSWR.data ?? {}),
    [searchTagsSWR.data]
  );

  const options: Option[] = Object.entries(tagsObj).map(
    ([category, items]) => ({
      label: category,
      value: category,
      disableCheckbox: true,
      children: items.map((item) => ({
        label: item,
        value: item,
      })),
    })
  );

  const category = useMemo(() => {
    if (searchTextDecoded) {
      for (const category in tagsObj) {
        if (tagsObj[category].includes(searchTextDecoded)) {
          return category;
        }
      }
    }
    return null;
  }, [searchTextDecoded, tagsObj]);

  const clearSearch = useCallback(() => {
    updateTextSearchTerm(null, history, match, 1, pageSize);
  }, [history, match, pageSize]);

  // For contractors only: clear current search when Request ID is cleared
  useEffect(() => {
    if (!requestID && isContractorToken) {
      clearSearch();
    }
  }, [requestID, isContractorToken, clearSearch]);

  // TODO: Change to this version when modifying to allow multiple tags at once:
  // const onChange: CascaderProps<Option, 'value', true>['onChange'] = (
  //   values
  // ) => {
  const onChange: CascaderProps<Option>['onChange'] = (
    values: (string | number)[] | undefined
  ) => {
    // When doing a new search, always reset pageNo to 1, but leave pageSize as-is
    setPageNo(1);

    if (!values) {
      clearSearch();
      return;
    }

    const [, item] = values;
    const encodedText = encodeURIComponent(item).replace(/%20/g, '+');
    changeURLParams({
      // Intentionally clear from the URL params that are added when an image is clicked
      logName: null,
      timestamp: null,
      camera_channel: null,
      bbox: null,
    });

    updateTextSearchTerm(encodedText, history, match, 1, pageSize);
  };

  return (
    <HorizLayout
      style={{ minWidth: '350px', marginLeft: '2em' }}
      ref={searchRef}
    >
      <SearchOutlined
        style={{ fontSize: '18px', marginRight: '4px', color: 'darkblue' }}
      />
      {searchTagsSWR.error ? (
        <Alert message="Failed to load Omnitag search tags" type="error" />
      ) : (
        <>
          {searchTagsSWR.isLoading ? (
            <Skeleton active title paragraph={false} />
          ) : (
            <Tooltip
              title="Enter keywords or tags to initiate a search"
              placement="topLeft"
            >
              <Cascader
                placeholder={
                  requestID || !isContractorToken
                    ? 'Select a Search Tag'
                    : 'You must select a RequestID before you can search'
                }
                style={{ width: '100%', height: '100' }}
                options={options}
                value={
                  category ? [category || '', searchTextDecoded] : undefined
                }
                onChange={onChange}
                // For contractors only: require RequestID before allowing search
                disabled={isContractorToken && !requestID}
                //  multiple // Enable this later when backend API can support multiple search tags at once
              />
            </Tooltip>
          )}
        </>
      )}
    </HorizLayout>
  );
};

export const updateTextSearchTerm = (
  encodedText: string | null,
  history: H.History<H.LocationState>,
  match: { path: string },
  pageNo: number,
  pageSize: number
): void => {
  const searchParams = new URLSearchParams(history.location.search);
  if (encodedText) {
    searchParams.set('search', encodedText);
  } else {
    searchParams.delete('search');
  }
  searchParams.set('pageNo', String(pageNo));
  searchParams.set('pageSize', String(pageSize));

  history.push({
    pathname: generatePath(match.path, {
      query: encodedText ? encodedText : undefined,
    }),
    search: searchParams.toString(),
  });
};
